// EXTERNAL DEPENDENCIES
import React from 'react'

// COMPONENTS
import { ContextualAlert } from '@paypalcorp/pp-react'
import FileUploader from '../../file-uploader'
import ImageCropper from '../../image-cropper'
import CoverPhotoDisplay from '../cover-photo-display'
import ProfilePhotoDisplay from '../profile-photo-display'

// HOOKS
import usePhotoUploading from '@/hooks/ui'

// STYLES
import styles from './styles.module.less'

// TYPES
import { PhotoTypes, UnformattedFptiOptions } from '@/types'

/**
 * Displays Rectangular and Round photo upload sections
 */
export default function PhotoUploadSection({
  isLoading,
  ariaLabelText,
  photoType,
  photoSrc,
  sheetTitle,
  isDisplayPhoto,
  needsToUploadCoverPhoto,
  customDisplay,
  errorMessage,
  setErrorMessage,
  handleEditPhotoClick,
  updatePhotoLocallyAndOpenCropScreen,
  isCoverPhoto,
  isEditingPhoto,
  isCroppingPhoto,
  closeCropScreenAndHandlePhotoRemoval,
  updatePhotoAndCloseCropScreen,
  updatePhotoValidity,
  updatedPhotoSrc,
  fpti,
  isOwnerPhoto,
}: Readonly<
  ReturnType<typeof usePhotoUploading> & {
    isDisplayPhoto?: boolean
    isLoading: boolean
    photoType: PhotoTypes
    sheetTitle: string
    customDisplay?: React.ReactElement
    fpti: {
      editFpti: string
      uploadFpti: string
      cropFpti: UnformattedFptiOptions
      selectNewPhotoFpti: UnformattedFptiOptions
      changeFpti: UnformattedFptiOptions
      saveFpti: UnformattedFptiOptions
    }
    isOwnerPhoto?: boolean
  }
>) {
  const clickFpti = handleEditPhotoClick ? fpti?.editFpti : fpti?.uploadFpti

  const coverPhotoImageContainer = (
    <CoverPhotoDisplay
      isDisplayPhoto={isDisplayPhoto}
      src={photoSrc}
      isLoading={isLoading}
      fpti={clickFpti}
      handleClick={needsToUploadCoverPhoto ? () => {} : handleEditPhotoClick}
      isOwnerPhoto={isOwnerPhoto}
    />
  )

  const profilePhotoAvatar = (
    <ProfilePhotoDisplay
      shouldShowOverlay
      isLoading={isLoading}
      src={photoSrc}
      fpti={clickFpti}
      handleClick={handleEditPhotoClick}
    />
  )

  const profileOrCoverPhotoUploader = (
    <FileUploader
      fpti={fpti?.selectNewPhotoFpti}
      ariaLabelText={ariaLabelText}
      keyName={`${photoType}-direct-click`}
      handleResult={updatePhotoLocallyAndOpenCropScreen}
      handleError={setErrorMessage}
    >
      {isCoverPhoto ? coverPhotoImageContainer : profilePhotoAvatar}
    </FileUploader>
  )
  const finalCoverPhotoDisplay = needsToUploadCoverPhoto
    ? profileOrCoverPhotoUploader
    : coverPhotoImageContainer

  const finalProfilePhotoDisplay = (
    <div className={styles.profilePhotoContainer}>
      {photoSrc ? profilePhotoAvatar : profileOrCoverPhotoUploader}
    </div>
  )

  const handleClose = () => {
    closeCropScreenAndHandlePhotoRemoval()
    updatePhotoValidity(photoType, true)
  }

  return (
    <div className={styles.photoSectionContainer}>
      {isEditingPhoto || isCroppingPhoto ? (
        <ImageCropper
          fpti={fpti}
          ariaLabelText={ariaLabelText}
          isCircularPhoto={!isCoverPhoto}
          title={sheetTitle}
          handleClose={handleClose}
          handleDelete={(_e) => closeCropScreenAndHandlePhotoRemoval(_e, true)}
          image={updatedPhotoSrc}
          handleResult={updatePhotoLocallyAndOpenCropScreen}
          handleSave={updatePhotoAndCloseCropScreen}
          handleError={setErrorMessage}
          isEditing={isEditingPhoto}
        />
      ) : null}
      {customDisplay ? (
        <FileUploader
          fpti={fpti?.selectNewPhotoFpti}
          ariaLabelText={ariaLabelText}
          keyName={`${photoType}-custom-display`}
          handleResult={updatePhotoLocallyAndOpenCropScreen}
          handleError={setErrorMessage}
        >
          {customDisplay}
        </FileUploader>
      ) : isCoverPhoto ? (
        finalCoverPhotoDisplay
      ) : (
        finalProfilePhotoDisplay
      )}
      {errorMessage && (
        <ContextualAlert type='error' className={styles.errorMessageContainer}>
          {errorMessage}
        </ContextualAlert>
      )}
    </div>
  )
}
